<template>
  <apexchart
    type="line"
    height="500"
    :options="chartOptions"
    :series="chartSeries"
  />
</template>

<script>
import apexchart from "vue-apexcharts"
import {uniq} from "lodash/array"
import {cloneDeep} from "lodash/lang"

export default {
  name: "FeedConsumptionGraph",
  components: {
    apexchart,
  },
  props: {
    graphData: {
      type: Object,
      default: {},
    },
    cdConsumptionTable: {
      type: Number,
      default: () => null,
    },
    graphShowType: {
      type: String,
      default: () => null,
    }
  },
  methods: {
    createRange(start, end) {
      if (!start && start != 0) return []
      if (!end) return []
      if ((end - start + 1) <= 0) return []
      
      return Array(end - start + 1)
        .fill()
        .map((_, idx) => start + idx)
    },
  },
  computed: {
    consumptionTable() {
      return this.$store.state.tabelasConsumoRacao
    },
    consumptionTableSeries() {
      let series = {}
      if (!this.cdConsumptionTable) return null
      
      const table = this.consumptionTable.find(
        (item) => item.idTabelaConsumoRacao === this.cdConsumptionTable
      )
      
      if (!table) return null
      
      let dailyConsumptionTable = []
      let initialDay = Math.min(...this.categories)
      let sumValue = 0
      let lastValue = 0
      
      if (!initialDay) return {}
      
      const sortedPointTable = table.pontos.sort((a, b) => {
        return a.nrDia - b.nrDia
      })
      
      const maxDay = Math.max(...this.categories)
      sortedPointTable.forEach((item) => {
        let currEndDay = item.nrDia
        if (item.nrDia >= maxDay) currEndDay = maxDay
        if (initialDay >= currEndDay) return
        if (this.graphShowType === "AC")
          lastValue = parseFloat(item.qtConsumo)
        this.createRange(initialDay, currEndDay).forEach((x) => {
          if (this.graphShowType === "AC")
            sumValue += lastValue
          else
            sumValue = parseFloat(item.qtConsumo)
          dailyConsumptionTable.push({
            day: x,
            value: sumValue.toFixed(3)
          })
          initialDay = item.nrDia + 1
        })
      })
      
      const dailyConsumptionTableSorted = dailyConsumptionTable
        .sort((a, b) => a.day - b.day)
      
      this.notFoundDays.forEach((day) => {
        const idx = dailyConsumptionTableSorted.findIndex(
          (item) => item.day === day
        )
        if (idx > -1) {
          dailyConsumptionTableSorted.splice(idx, 1)
        }
      })
      series = {
        name: "Consumo esperado",
        type: "line",
        data: dailyConsumptionTableSorted.map(item => item.value),
      }
      return series
    },
    zippedAvg() {
      const clonedGraphData = cloneDeep(this.graphData)
      const graphDataKeys = Object.keys(clonedGraphData)
      if (!graphDataKeys.length) return []
      
      const zippedList = []
      this.categories.forEach((day) => {
        const dayAvgList = []
        graphDataKeys.forEach((key) => {
          const data = clonedGraphData[key].find((item) => item["dia"] === day)
          if (data) dayAvgList.push(data["avg"])
        })
        zippedList.push(
          (
            dayAvgList.reduce((acc, i) => acc + i, 0) / dayAvgList.length
          ).toFixed(3)
        )
      })
      return zippedList
    },
    notFoundDays() {
      const graphDataKeys = Object.keys(this.graphData)
      if (!graphDataKeys.length) return []
      
      const allDays = Object.keys(this.graphData).flatMap((key) =>
        this.graphData[key].flatMap((item) => item["dia"])
      )
      const minDay = Math.min(...allDays)
      const maxDay = Math.max(...allDays)
      
      let allDaysArr = this.createRange(minDay, maxDay)
      
      let notFoundDays = []
      allDaysArr.forEach(day => {
        Object.keys(this.graphData).forEach(key => {
          const idx = this.graphData[key].findIndex(item => item["dia"] === day)
          if (idx === -1) notFoundDays.push(day)
        })
      })
      
      notFoundDays = uniq(notFoundDays)
      return notFoundDays
    },
    categories() {
      const graphDataKeys = Object.keys(this.graphData)
      if (!graphDataKeys.length) return []
      
      const allDays = Object.keys(this.graphData).flatMap((key) =>
        this.graphData[key].flatMap((item) => item["dia"])
      )
      
      if (!allDays.length) return []
      
      const minDay = Math.min(...allDays)
      const maxDay = Math.max(...allDays)
      
      let allDaysArr = this.createRange(minDay, maxDay)
      
      let notFoundDays = []
      allDaysArr.forEach(day => {
        Object.keys(this.graphData).forEach(key => {
          const idx = this.graphData[key].findIndex(item => item["dia"] === day)
          if (idx === -1) notFoundDays.push(day)
        })
      })
      
      notFoundDays = uniq(notFoundDays)
      notFoundDays.forEach(day => {
        const idx = allDaysArr.findIndex(item => item === day)
        if (idx > -1) allDaysArr.splice(idx, 1)
      })
      allDaysArr = allDaysArr.sort((a, b) => a - b)
      return allDaysArr
    },
    chartOptions() {
      const chart = {
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true
        }
      }
      const colors = ["#10303D", "#4CAF50", "#2196F3", "#ff5429"]
      const stroke = {
        curve: "smooth",
        dashArray: [4, 0],
        width: [3, 1],
        colors: ["#10303D", "#4CAF50", "#2196F3", "#ff5429"],
      }
      const xaxis = {
        type: "numeric",
        categories: this.categories,
        title: {
          text: "Semana",
        },
        labels: {
          style: {
            colors: "#000000de",
          },
          formatter: function (val) {
            return val.toFixed(0)
          },
        },
      }
      const yaxis = [
      {
        axisBorder: {
          show: false,
        },
      }]
      const legends = {
        position: "top",
        horizontalAlign: "center",
        fontSize: "14px",
        labels: {
          useSeriesColors: false,
        },
      }
      return {
        chart: chart,
        colors: colors,
        stroke: stroke,
        xaxis: xaxis,
        yaxis: yaxis,
        legends: legends,
      }
    },
    chartSeries() {
      const clonedGraphData = cloneDeep(this.graphData)
      const graphDataKeys = Object.keys(clonedGraphData)
      if (!graphDataKeys.length) return []
      const seriesList = []
      seriesList.push({
        name: "Média dos lotes",
        type: "line",
        data: this.zippedAvg,
      })
      if (this.consumptionTableSeries)
        seriesList.push(this.consumptionTableSeries)
      graphDataKeys.forEach((key) => {
        let displayKey = key
          .replace("CLOSING_", "Fechamento ")
          .replace("ACCOMMODATION_", "Alojamento ")
        
        this.notFoundDays.forEach((day) => {
          const idx = clonedGraphData[key].findIndex(
            (item) => item["dia"] === day
          )
          if (idx > -1) {
            clonedGraphData[key].splice(idx, 1)
          }
        })
        
        clonedGraphData[key] = clonedGraphData[key].sort(
          (a, b) => a["dia"] - b["dia"]
        )
        seriesList.push({
          name: displayKey,
          type: "column",
          data: clonedGraphData[key].map((item) => item["avg"].toFixed(3)),
        })
      })
      
      return seriesList
    },
  },
}
</script>

<style scoped></style>